import { ApiServiceApplicationDocumentKind } from "@rmp/core/types/services/serviceDocumentKind";
import { ApiEnterprisePropertyType } from "@rmp/api-gateway";
import { BaseServiceCapabilities } from "@rmp/core/types/service/BaseServiceCapabilities";
import { ApiServiceType } from "@rmp/core/types/services/ApiServiceType";
import { SERVICE_DOCUMENT_GROUPS, SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS, ServiceDocumentGroupCode } from "@rmp/core/types/services/workflow/serviceDocumentWorkflow";
import { ServiceWorkflowTransitionCode } from "@rmp/core/types/services/workflow/serviceWorkflow";
import i18n from "@rmp/core/plugins/i18n/core";

export type ServiceWorkflowResourceKind =
	| "tugboat"
	| "crewboat"
	| "floatingCrane"
	| "pier"
	| "icebreaker"
	| "environmentalVessel"
	| "hydrographicBoat";

export type ServiceWorkflowProfile = {
	hasResourceAssignmentStep: boolean;
	resourceKind?: ServiceWorkflowResourceKind;
	bookingMode?: "None" | "Full" | "Partial";
};

export type ServiceDocumentWorkflowGroup = ServiceDocumentGroupCode;

export type ServiceDocumentWorkflowProfile = {
	groups: Record<ServiceDocumentWorkflowGroup, readonly ApiServiceApplicationDocumentKind[]>;
	accountingGroups: readonly ["invoice", "factureInvoice"];
	transitionDocumentGroups: Partial<Record<ServiceWorkflowTransitionCode, readonly ServiceDocumentWorkflowGroup[]>>;
	applicationSubmissionDocuments: readonly ApiServiceApplicationDocumentKind[];
};

export type ServiceInstructionCapabilities = {
	agreementLinkStep: boolean;
	agreementSelectionStep: boolean;
	enterprisePropertyStep: boolean;
	dateAndTimeStep: boolean;
	finalCostStep: boolean;
	uploadAgreementsStep: boolean;
};

export type ServiceCapabilities = BaseServiceCapabilities & {
	hasAgreementDetails: boolean;
	hasDrinkingWaterBunkeringParameters: boolean;
	requiresDrinkingWaterBunkeringParameters: boolean;
	hasServiceProvisionSpecialist: boolean;
	requiresServiceProvisionSpecialist: boolean;
	instructions: ServiceInstructionCapabilities;
};

export type ServiceParameters = {
	startDate: boolean;
	endDate: boolean;
	startTime: boolean;
	endTime: boolean;
	startTimeRequired: boolean;
	endTimeRequired: boolean;
	endDestination: boolean;
	startDestination: boolean;
	customStartDestination: boolean;
	customEndDestination: boolean;
};

export type ServiceCapabilityRequirement = "Disabled" | "Optional" | "Required";

export type ServiceUI = {
	assignButtonTextKey?: string;
	tariffsTabTextKey?: string;
	icon?: string;
};

export type ServiceTariffConfig = {
	operationsKey?: string;
	usageKey?: string;
	propertyIdKey?: string;
};

/**
 * Структурный аналог профиля, возвращаемого с ServiceDefinition и
 * ServiceApplication. Core намеренно не зависит от сгенерированных
 * пакетов шлюзов.
 */
export type BackendServiceDefinitionProfile = {
	tariffMode: "None" | "Operation";
	applicationSubmissionCounterpartyBoatRequirement: "Disabled" | "Optional" | "Required";
	supportedPropertyType?: ApiEnterprisePropertyType | `${ApiEnterprisePropertyType}` | null;
	destinationMode: "None" | "Catalog" | "Custom";
	applicationSubmissionDocumentKinds: string[];
	applicationSubmissionDocumentsRequirement: "Disabled" | "Optional" | "Required";
	bookingMode: "None" | "Full" | "Partial";
	fromTimeRequirement: ServiceCapabilityRequirement;
	tillTimeRequirement: ServiceCapabilityRequirement;
	serviceProvisionSpecialist: ServiceCapabilityRequirement;
	drinkingWaterBunkeringParameters: ServiceCapabilityRequirement;
};

export type ServiceProfile = {
	serviceType: ApiServiceType;
	priority: number;
	capabilities: ServiceCapabilities;
	applicationSubmissionParameters: ServiceParameters;
	provisionParameters: ServiceParameters;
	workflow: ServiceWorkflowProfile;
	documents: ServiceDocumentWorkflowProfile;
	ui: ServiceUI;
	tariffConfig?: ServiceTariffConfig;
};

export type ServicePresentation = Pick<ServiceProfile, "serviceType" | "priority" | "ui">;

export const SERVICE_DOCUMENT_WORKFLOW_PROFILE: ServiceDocumentWorkflowProfile = {
	groups: {
		act: [SERVICE_DOCUMENT_GROUPS.act.kind],
		invoice: [SERVICE_DOCUMENT_GROUPS.invoice.kind],
		factureInvoice: [SERVICE_DOCUMENT_GROUPS.factureInvoice.kind]
	},
	accountingGroups: ["invoice", "factureInvoice"],
	transitionDocumentGroups: {
		AttachAct: SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS.AttachAct.requiredGroups,
		SignActByEnterprise: SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS.SignActByEnterprise.signGroups,
		AttachAccountingDocuments: SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS.AttachAccountingDocuments.requiredGroups,
		SignAccountingDocumentsByEnterprise: SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS.SignAccountingDocumentsByEnterprise.signGroups,
		SignActByCounterparty: SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS.SignActByCounterparty.signGroups
	},
	applicationSubmissionDocuments: []
};

const PROPERTY_TARIFF_CONFIG: ServiceTariffConfig = {
	operationsKey: "operations",
	usageKey: "usage",
	propertyIdKey: "propertyId"
};
export const SERVICE_PRESENTATIONS: Partial<Record<ApiServiceType, ServicePresentation>> = {
	[ApiServiceType.CrewBoatService]: {
		serviceType: ApiServiceType.CrewBoatService,
		priority: 0,
		ui: {
			icon: "services/ico_crew-boat-outline",
			assignButtonTextKey: "buttons.assignCrewboat",
			tariffsTabTextKey: "subheaders.crewboats"
		}
	},
	[ApiServiceType.PilotTransfer]: {
		serviceType: ApiServiceType.PilotTransfer,
		priority: 1,
		ui: {
			icon: "services/ico_pilot-transfer-services",
			assignButtonTextKey: "buttons.assign",
			tariffsTabTextKey: "subheaders.tariffs"
		}
	},
	[ApiServiceType.TugService]: {
		serviceType: ApiServiceType.TugService,
		priority: 2,
		ui: {
			icon: "services/ico_tug-boat-outline",
			assignButtonTextKey: "buttons.assignTugboat",
			tariffsTabTextKey: "subheaders.tugboats"
		}
	},
	[ApiServiceType.DrinkingWater]: {
		serviceType: ApiServiceType.DrinkingWater,
		priority: 3,
		ui: { tariffsTabTextKey: "subheaders.operations", icon: "services/ico_drinking-water-services" }
	},
	[ApiServiceType.DeviationRemoval]: {
		serviceType: ApiServiceType.DeviationRemoval,
		priority: 4,
		ui: { tariffsTabTextKey: "subheaders.operations", icon: "services/ico_deviation-removal-services" }
	},
	[ApiServiceType.IndividualIcebreakerAssistance]: {
		serviceType: ApiServiceType.IndividualIcebreakerAssistance,
		priority: 5,
		ui: {
			icon: "services/ico_icebreaking-services",
			assignButtonTextKey: "buttons.assign",
			tariffsTabTextKey: "subheaders.icebreakers"
		}
	},
	[ApiServiceType.InformationService]: {
		serviceType: ApiServiceType.InformationService,
		priority: 6,
		ui: { tariffsTabTextKey: "subheaders.operations", icon: "services/ico_info-outline" }
	},
	[ApiServiceType.Ecological]: {
		serviceType: ApiServiceType.Ecological,
		priority: 7,
		ui: {
			icon: "services/ico_ecological-services",
			assignButtonTextKey: "buttons.assignEnvironmentalVessels",
			tariffsTabTextKey: "subheaders.environmentalVessels"
		}
	},
	[ApiServiceType.PierService]: {
		serviceType: ApiServiceType.PierService,
		priority: 8,
		ui: {
			icon: "services/ico_anchor-outline",
			assignButtonTextKey: "buttons.assignPier",
			tariffsTabTextKey: "subheaders.piers"
		}
	},
	[ApiServiceType.IcePilotService]: {
		serviceType: ApiServiceType.IcePilotService,
		priority: 9,
		ui: {
			icon: "services/ico_ice-pilotage-services",
			assignButtonTextKey: "buttons.assignCrewboat",
			tariffsTabTextKey: "subheaders.operations"
		}
	},
	[ApiServiceType.LinkspanBridge]: {
		serviceType: ApiServiceType.LinkspanBridge,
		priority: 10,
		ui: { tariffsTabTextKey: "subheaders.operations", icon: "services/ico_linkspan-bridge-services" }
	},
	[ApiServiceType.GeodeticHydrographicService]: {
		serviceType: ApiServiceType.GeodeticHydrographicService,
		priority: 11,
		ui: {
			icon: "services/ico_geodetic-outline",
			assignButtonTextKey: "buttons.assignHydrographicBoats",
			tariffsTabTextKey: "subheaders.hydrographicBoats"
		}
	},
	[ApiServiceType.DredgingService]: {
		serviceType: ApiServiceType.DredgingService,
		priority: 12,
		ui: { icon: "services/ico_dredging-outline" }
	},
	[ApiServiceType.SnoInstallationService]: {
		serviceType: ApiServiceType.SnoInstallationService,
		priority: 13,
		ui: { icon: "services/ico_maintenance-outline" }
	},
	[ApiServiceType.FloatingCraneService]: {
		serviceType: ApiServiceType.FloatingCraneService,
		priority: 14,
		ui: {
			icon: "services/ico_floating-crane-outline",
			assignButtonTextKey: "buttons.assign",
			tariffsTabTextKey: "subheaders.floatingCranes"
		}
	},
	[ApiServiceType.Icebreaking]: {
		serviceType: ApiServiceType.Icebreaking,
		priority: 15,
		ui: {
			icon: "services/ico_icebreaking-services",
			tariffsTabTextKey: "subheaders.icebreakers"
		}
	},
	[ApiServiceType.Consulting]: {
		serviceType: ApiServiceType.Consulting,
		priority: 16,
		ui: { icon: "services/ico_consulting-services" }
	}
};

export type ApiServiceTypeLike = ApiServiceType | `${ApiServiceType}`;

function normalizeServiceType(serviceType: ApiServiceTypeLike): ApiServiceType {
	const presentation = SERVICE_PRESENTATIONS[serviceType as ApiServiceType];
	
	if(!presentation) {
		throw new Error(i18n.t("errors.servicePresentationNotConfigured", { serviceType }).toString());
	}
	
	return serviceType as ApiServiceType;
}

const getResourceKindFromPropertyType = (
	propertyType?: ApiEnterprisePropertyType | `${ApiEnterprisePropertyType}` | null
): ServiceWorkflowResourceKind | undefined => {
	switch (propertyType) {
		case ApiEnterprisePropertyType.Tugboat:
			return "tugboat";
		case ApiEnterprisePropertyType.Crewboat:
			return "crewboat";
		case ApiEnterprisePropertyType.FloatingCrane:
			return "floatingCrane";
		case ApiEnterprisePropertyType.Pier:
			return "pier";
		case ApiEnterprisePropertyType.Icebreaker:
			return "icebreaker";
		case ApiEnterprisePropertyType.EnvironmentalVessel:
			return "environmentalVessel";
		case ApiEnterprisePropertyType.HydrographicBoat:
			return "hydrographicBoat";
		default:
			return undefined;
	}
};

/**
 * Формирует итоговое представление фронтенда из профиля бэкенда,
 * который является источником истины. Таблица ServiceType выше
 * содержит только данные представления.
 */
const applyBackendProfile = (
	presentation: ServicePresentation,
	backend: BackendServiceDefinitionProfile
): ServiceProfile => {
	const propertyType = backend.supportedPropertyType as ApiEnterprisePropertyType | undefined;
	const resourceKind = getResourceKindFromPropertyType(propertyType);
	const hasTariffs = backend.tariffMode !== "None";
	const hasOperations =
		backend.tariffMode === "Operation" || backend.supportedPropertyType != null;
	const hasCounterpartyBoat =
		backend.applicationSubmissionCounterpartyBoatRequirement !== "Disabled";
	const hasApplicationSubmissionDocuments =
		backend.applicationSubmissionDocumentsRequirement !== "Disabled";
	const requiresDocuments = backend.applicationSubmissionDocumentsRequirement === "Required";
	const destinationParameters = {
		startDestination: backend.destinationMode === "Catalog",
		endDestination: backend.destinationMode === "Catalog",
		customStartDestination: backend.destinationMode === "Custom",
		customEndDestination: backend.destinationMode === "Custom"
	};
	
	return {
		...presentation,
		capabilities: {
			propertyType: propertyType ?? null,
			hasTariffs,
			hasOperations,
			hasCounterpartyBoat,
			hasApplicationSubmissionDocuments,
			isCounterpartyBoatOptional:
				backend.applicationSubmissionCounterpartyBoatRequirement === "Optional",
			requiresDocumentsOnApplicationSubmission: requiresDocuments,
			hasTimePlaceDetails: true,
			hasAgreementDetails: true,
			hasDrinkingWaterBunkeringParameters:
				backend.drinkingWaterBunkeringParameters !== "Disabled",
			requiresDrinkingWaterBunkeringParameters:
				backend.drinkingWaterBunkeringParameters === "Required",
			hasServiceProvisionSpecialist:
				backend.serviceProvisionSpecialist !== "Disabled",
			requiresServiceProvisionSpecialist:
				backend.serviceProvisionSpecialist === "Required",
			instructions: {
				agreementLinkStep: true,
				agreementSelectionStep: true,
				enterprisePropertyStep: !!resourceKind,
				dateAndTimeStep: true,
				finalCostStep: hasTariffs,
				uploadAgreementsStep: hasApplicationSubmissionDocuments
			}
		},
		applicationSubmissionParameters: {
			startDate: true,
			endDate: false,
			startTime: backend.fromTimeRequirement !== "Disabled",
			endTime: false,
			startTimeRequired: backend.fromTimeRequirement === "Required",
			endTimeRequired: false,
			...destinationParameters
		},
		provisionParameters: {
			startDate: true,
			endDate: true,
			startTime: backend.fromTimeRequirement !== "Disabled",
			endTime: backend.tillTimeRequirement !== "Disabled",
			startTimeRequired: backend.fromTimeRequirement === "Required",
			endTimeRequired: backend.tillTimeRequirement === "Required",
			...destinationParameters
		},
		workflow: {
			hasResourceAssignmentStep: !!resourceKind,
			resourceKind,
			bookingMode: backend.bookingMode
		},
		documents: {
			...SERVICE_DOCUMENT_WORKFLOW_PROFILE,
			applicationSubmissionDocuments:
				backend.applicationSubmissionDocumentKinds as ApiServiceApplicationDocumentKind[]
		},
		tariffConfig: hasTariffs ? PROPERTY_TARIFF_CONFIG : undefined
	};
};

export function getServiceProfile(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): ServiceProfile {
	const normalizedServiceType = normalizeServiceType(serviceType);
	return applyBackendProfile(SERVICE_PRESENTATIONS[normalizedServiceType]!, backend);
}

export function getServicePresentation(serviceType: ApiServiceTypeLike): ServicePresentation {
	return SERVICE_PRESENTATIONS[normalizeServiceType(serviceType)]!;
}

export function getServiceTypes(): ApiServiceType[] {
	return Object.values(SERVICE_PRESENTATIONS)
				 .sort((a, b) => a!.priority - b!.priority)
				 .map(x => x!.serviceType);
}

export function hasResourceAssignmentStep(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): boolean {
	return getServiceProfile(serviceType, backend).workflow.hasResourceAssignmentStep;
}

export function getResourceKind(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): ServiceWorkflowResourceKind | undefined {
	return getServiceProfile(serviceType, backend).workflow.resourceKind;
}

export function hasAssignableResource(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): boolean {
	const { workflow } = getServiceProfile(serviceType, backend);
	return workflow.hasResourceAssignmentStep && !!workflow.resourceKind;
}

export function hasRequestedResourceSelection(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): boolean {
	const profile = getServiceProfile(serviceType, backend);
	return !!profile.workflow.resourceKind;
}

export function getServiceDocumentWorkflowProfile(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): ServiceDocumentWorkflowProfile {
	return getServiceProfile(serviceType, backend).documents;
}

export function getServiceWorkflowProfile(
	serviceType: ApiServiceTypeLike,
	backend: BackendServiceDefinitionProfile
): ServiceWorkflowProfile {
	return getServiceProfile(serviceType, backend).workflow;
}
